'use client'

import { useState, useEffect, useRef } from 'react'
import { Application, User } from '@/types'
import { moderateMessage } from '@/lib/contentModeration'
import { useSafeDate } from '@/hooks/useSafeDate'
import { useOptimizedChat } from '@/hooks/useOptimizedChat'
import ClientOnly from '@/components/ClientOnly'
import ChatSkeleton from '@/components/ChatSkeleton'

interface LandlordChatInterfaceProps {
  application: Application & {
    property: { title: string }
    client: User
  }
  currentUser: User
  onClose: () => void
}

// MessageWithUser interface moved to useOptimizedChat hook

export default function LandlordChatInterface({ application, currentUser, onClose }: LandlordChatInterfaceProps) {
  const [newMessage, setNewMessage] = useState('')
  const [clientUser, setClientUser] = useState<User | null>(null)
  const { formatTime, formatDateWithToday } = useSafeDate()
  
  // Use optimized chat hook for better performance
  const {
    messages,
    loading,
    sending,
    messagesEndRef,
    loadMessages,
    sendMessage
  } = useOptimizedChat({
    applicationId: application.id,
    currentUserId: currentUser.id
  })

  // Get client user info
  useEffect(() => {
    setClientUser(application.client)
  }, [application])

  // Message loading handled by useOptimizedChat hook

  // Utility to compute highest severity
  const getHighestSeverity = (severities: Array<'high' | 'medium' | 'low'>): 'high' | 'medium' | 'low' => {
    if (severities.includes('high')) return 'high'
    if (severities.includes('medium')) return 'medium'
    return 'low'
  }

  // Send admin notification
  const notifyAdmin = async (title: string, message: string, priority: 'low' | 'medium' | 'high' | 'urgent' = 'high') => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      await fetch(`${apiUrl}/admin/notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          admin_id: null,
          type: 'system',
          title,
          message,
          priority,
          status: 'unread',
          action_required: true,
          action_url: '/admin/dashboard'
        })
      })
    } catch (e) {
      console.error('Admin notify error:', e)
    }
  }

  // Message handling optimized via useOptimizedChat hook
  
  // Load messages on mount
  useEffect(() => {
    loadMessages()
  }, [loadMessages])



  // Send message with optimistic UI and content moderation
  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return

    const receiverId = application.client.id

    try {
      // Apply content moderation
      const moderation = moderateMessage(newMessage.trim())

      // Simple rule: any sensitive/flagged or blocked content should not be sent – just clear the input
      if (moderation.isBlocked || moderation.isFlagged) {
        setNewMessage('')
        return
      }

      // Use optimistic sending for instant UI update
      await sendMessage(newMessage.trim())
      setNewMessage('') // Clear input only on success
      
      // Handle moderation logging asynchronously (don't block UI)
      if (moderation.isFlagged) {
        const severity = getHighestSeverity(moderation.flaggedContent.map(f => f.severity))
        
        // Log moderation (async)
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
        fetch(`${apiUrl}/admin/content-moderation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message_id: null, // Will be updated later
            application_id: application.id,
            sender_id: currentUser.id,
            receiver_id: receiverId,
            original_message: newMessage.trim(),
            sanitized_message: moderation.sanitizedMessage,
            moderation_result: moderation,
            flagged_content: moderation.flaggedContent,
            action_taken: 'flagged',
            severity,
            reason: moderation.flaggedContent.map(f => f.reason).join('; ')
          })
        }).then(() => {
          // Notify admin if high severity (async)
          const hasHighSeverity = moderation.flaggedContent.some(fc => fc.severity === 'high')
          if (hasHighSeverity) {
            notifyAdmin(
              'High-risk content detected',
              `User ${currentUser.email} sent potentially harmful content. Please review immediately.`
            )
          }
        })
      }
    } catch (error) {
      console.error('Error:', error)
      alert('Failed to send message')
    }
  }

  // File upload support
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0])
    }
  }

  const clearSelectedFile = () => setSelectedFile(null)

  const sendAttachment = async () => {
    if (!selectedFile || uploading) return
    setUploading(true)
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        alert('Please log in to send attachments')
        return
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'

      // 1) Upload file via backend (Cloudinary)
      const formData = new FormData()
      formData.append('file', selectedFile)

      const uploadRes = await fetch(`${apiUrl}/upload/single`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })

      const uploadData = await uploadRes.json().catch(() => ({}))
      const publicUrl = uploadData?.file?.url || uploadData?.url
      if (!uploadRes.ok || !publicUrl) {
        throw new Error(uploadData?.message || 'Failed to upload attachment')
      }

      const composedMessage = newMessage.trim() || `Attachment: ${selectedFile.name}`

      const moderation = moderateMessage(composedMessage)

      // Simple rule for attachments too: any sensitive/flagged or blocked content should not be sent – just clear
      if (moderation.isBlocked || moderation.isFlagged) {
        setNewMessage('')
        setSelectedFile(null)
        setUploading(false)
        return
      }

      const messageType = selectedFile.type?.startsWith('image/') ? 'image' : 'file'
      const msgRes = await fetch(`${apiUrl}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          applicationId: application.id,
          message: composedMessage,
          messageType,
          attachments: [{
            filename: selectedFile.name,
            originalName: selectedFile.name,
            url: publicUrl,
            mimeType: selectedFile.type,
            size: selectedFile.size
          }]
        })
      })

      if (!msgRes.ok) {
        const err = await msgRes.json().catch(() => ({}))
        throw new Error(err?.message || 'Failed to send attachment')
      }

      setNewMessage('')
      setSelectedFile(null)
      await loadMessages(true)
    } catch (error) {
      console.error('Attachment error:', error)
    } finally {
      setUploading(false)
    }
  }

  // Parse messages that contain our attachment format:
  // Optional text, then a line starting with "Attachment: <filename>" and next line with a URL
  const parseAttachment = (text: string): { intro?: string; filename: string; url: string } | null => {
    // Normalize newlines
    const normalized = text.replace(/\r\n/g, '\n')
    // Find "Attachment: filename" (case-insensitive)
    const attachMatch = normalized.match(/attachment:\s*(.+)/i)
    if (!attachMatch) return null
    const filename = (attachMatch[1] || '').trim()
    // Find first URL after the attachment line, or any URL
    const urlMatch = normalized.slice(attachMatch.index || 0).match(/https?:\/\/\S+/i) || normalized.match(/https?:\/\/\S+/i)
    if (!urlMatch) return null
    const url = urlMatch[0]
    const introText = normalized.slice(0, attachMatch.index || 0).trim()
    return { intro: introText || undefined, filename, url }
  }

  const isImageUrl = (url: string) => /(\.jpg$|\.jpeg$|\.png$|\.gif$|\.webp$)/i.test(url)

  const buildDownloadUrl = (url: string, filename: string) => {
    const hasQuery = url.includes('?')
    const suffix = `download=${encodeURIComponent(filename || 'attachment')}`
    return `${url}${hasQuery ? '&' : '?'}${suffix}`
  }

  // Date formatting functions moved to useSafeDate hook

  const getClientDisplayName = (client: User | null) => {
    if (!client) return 'Unknown Client'
    if (client.kyc_data?.firstName && client.kyc_data?.lastName) {
      return `${client.kyc_data.firstName} ${client.kyc_data.lastName}`
    }
    return client.email || 'Client'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-3">
            {/* Avatar commented out as requested */}
            {false && (
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold">
                {clientUser?.email?.charAt(0).toUpperCase() || 'C'}
              </div>
            )}
            <div>
              <h3 className="font-semibold text-gray-900">
                {getClientDisplayName(clientUser)}
              </h3>
              <p className="text-sm text-gray-500">
                {application.property.title} - {application.status}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages Area */}
        {loading ? (
          <ChatSkeleton />
        ) : messages.length === 0 ? (
          <div className="flex-1 flex justify-center items-center text-gray-500">
            <div className="text-center">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-lg font-medium">No messages yet</p>
              <p className="text-sm text-gray-400">Start the conversation below!</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {
            messages.map((message, index) => {
              // Normalize IDs for comparison (handle both _id and id formats)
              const normalizedSenderId = String(message.sender_id || '').trim()
              const normalizedCurrentUserId = String(currentUser.id || (currentUser as any)._id || '').trim()
              const isOwnMessage = normalizedSenderId === normalizedCurrentUserId
              const senderRole = message.sender?.role || 'client'
              const showDate = index === 0 || 
                formatDateWithToday(message.created_at) !== formatDateWithToday(messages[index - 1]?.created_at)

              // Determine background color based on sender role
              const getMessageBgColor = () => {
                if (senderRole === 'landlord') {
                  return 'bg-green-600 text-white'
                } else if (senderRole === 'client') {
                  return 'bg-blue-600 text-white'
                }
                return 'bg-gray-100 text-gray-900'
              }

              return (
                <div key={message.id}>
                  {showDate && (
                    <div className="flex justify-center mb-4">
                      <span className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                        <ClientOnly fallback="...">
                          {formatDateWithToday(message.created_at)}
                        </ClientOnly>
                      </span>
                    </div>
                  )}
                  <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-xs lg:max-w-md ${isOwnMessage ? 'items-end' : 'items-start'} flex flex-col`}>
                      {/* Sender name/role badge */}
                      {!isOwnMessage && (
                        <div className="mb-1 px-2">
                          <span className="text-xs font-medium text-gray-600">
                            {message.sender?.firstName && message.sender?.lastName
                              ? `${message.sender.firstName} ${message.sender.lastName}`
                              : message.sender?.email?.split('@')[0] || 'Unknown'}
                            {message.sender?.role && (
                              <span className="ml-1 text-gray-400">
                                ({message.sender.role === 'landlord' ? 'Landlord' : message.sender.role === 'client' ? 'Client' : message.sender.role})
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                      <div className={`px-4 py-3 rounded-lg ${getMessageBgColor()}`}>
                        {(() => {
                          const attachments = (message as any).attachments as any[] | undefined
                          if (attachments && attachments.length > 0) {
                            return (
                              <div className="space-y-2">
                                {message.content && (
                                  <p className="text-sm whitespace-pre-line">{message.content}</p>
                                )}
                                <div className={`border ${senderRole === 'landlord' ? 'border-green-500/40' : senderRole === 'client' ? 'border-blue-500/40' : 'border-gray-300'} rounded-md p-3 bg-white text-gray-900`}>
                                  {attachments.map((att, idx) => {
                                    const url = att?.url
                                    const filename = att?.originalName || att?.filename || `attachment-${idx + 1}`
                                    if (!url) return null
                                    return (
                                      <div key={`${url}-${idx}`} className="flex items-start space-x-3">
                                        <div className={`shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${senderRole === 'landlord' ? 'bg-green-600 text-white' : senderRole === 'client' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}>
                                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 115.66 5.66l-9.2 9.2a2 2 0 01-2.83-2.83l8.49-8.49" />
                                          </svg>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                          <p className="text-sm font-medium truncate">{filename}</p>
                                          {isImageUrl(url) && (
                                            <div className="mt-2">
                                              <a href={url} target="_blank" rel="noopener noreferrer">
                                                <div className="relative max-h-40 w-auto">
                                                  <img 
                                                    src={url} 
                                                    alt={filename} 
                                                    className="rounded-md object-cover border" 
                                                    style={{ maxHeight: '10rem', width: 'auto' }}
                                                  />
                                                </div>
                                              </a>
                                            </div>
                                          )}
                                          <div className="mt-2">
                                            <a
                                              href={buildDownloadUrl(url, filename)}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              download
                                              className={`inline-flex items-center text-xs underline ${senderRole === 'landlord' ? 'text-green-800 bg-green-50' : senderRole === 'client' ? 'text-blue-800 bg-blue-50' : 'text-gray-700 bg-gray-50'} px-2 py-1 rounded`}
                                            >
                                              Download attachment
                                            </a>
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )
                          }

                          const parsed = parseAttachment(message.content)
                          if (parsed) {
                            return (
                              <div className="space-y-2">
                                {parsed.intro && (
                                  <p className="text-sm whitespace-pre-line">{parsed.intro}</p>
                                )}
                                <div className={`border ${senderRole === 'landlord' ? 'border-green-500/40' : senderRole === 'client' ? 'border-blue-500/40' : 'border-gray-300'} rounded-md p-3 bg-white text-gray-900`}>
                                  <div className="flex items-start space-x-3">
                                    <div className={`shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${senderRole === 'landlord' ? 'bg-green-600 text-white' : senderRole === 'client' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}>
                                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 115.66 5.66l-9.2 9.2a2 2 0 01-2.83-2.83l8.49-8.49" />
                                      </svg>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium truncate">{parsed.filename}</p>
                                      {isImageUrl(parsed.url) && (
                                        <div className="mt-2">
                                          <a href={parsed.url} target="_blank" rel="noopener noreferrer">
                                            <div className="relative max-h-40 w-auto">
                                              <img 
                                                src={parsed.url} 
                                                alt={parsed.filename} 
                                                className="rounded-md object-cover border" 
                                                style={{ maxHeight: '10rem', width: 'auto' }}
                                              />
                                            </div>
                                          </a>
                                        </div>
                                      )}
                                      <div className="mt-2">
                                        <a
                                          href={buildDownloadUrl(parsed.url, parsed.filename)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          download
                                          className={`inline-flex items-center text-xs underline ${isOwnMessage ? 'text-green-800 bg-green-50' : 'text-gray-700 bg-gray-50'} px-2 py-1 rounded`}
                                        >
                                          Download attachment
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )
                          }
                          return <p className="text-sm whitespace-pre-line">{message.content}</p>
                        })()}

                        <p className={`text-xs mt-2 flex items-center ${
                          isOwnMessage ? 'text-green-100' : 'text-gray-500'
                        }`}>
                        <ClientOnly fallback="...">
                          {formatTime(message.created_at)}
                          {!isOwnMessage && message.isRead === false && (
                            <span className="ml-2">•</span>
                          )}
                          {(message as unknown as { _optimistic?: boolean })._optimistic && (
                            <span className={`ml-2 flex items-center ${senderRole === 'landlord' ? 'text-green-200' : 'text-blue-200'}`}>
                              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span className="ml-1">Sending...</span>
                            </span>
                          )}
                        </ClientOnly>
                      </p>
                    </div>
                  </div>
                  </div>
                </div>
              )
            })}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Message Input + Upload */}
        <div className="border-t p-4 space-y-2">
          {/* Selected file pill + send */}
          {(selectedFile || uploading) && (
            <div className="flex items-center space-x-2">
              <span className="max-w-xs truncate px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 border">
                {uploading ? 'Uploading…' : selectedFile?.name}
              </span>
              <button
                onClick={sendAttachment}
                disabled={!selectedFile || uploading}
                className="px-3 py-1.5 bg-gray-800 text-white rounded-md hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed text-xs"
              >
                {uploading ? 'Please wait' : 'Send attachment'}
              </button>
              {!uploading && selectedFile && (
                <button
                  onClick={clearSelectedFile}
                  className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900"
                  title="Remove"
                >
                  ✕
                </button>
              )}
            </div>
          )}

          {/* Compose row */}
          <div className="flex items-center space-x-2">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              className="hidden"
            />
            {/* Plus icon button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              title="Attach file"
              className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>

            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Type your message..."
              className="flex-1 px-4 py-2 border text-black border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              disabled={sending}
            />
            <button
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sending}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
} 

